/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { Link } from "@packages/framework";
import type {
  DeeplinkSchema,
  GetURLFromDeeplinkProps,
  Images,
} from "@packages/sdk";
import { getURLFromDeeplink } from "@packages/sdk";
import type { MouseEventHandler } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  constantSemanticColors,
  numericPixels,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { useThemeValues } from "../../../../hooks/useThemeValues";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import { ChevronRightIcon } from "../../../_base/icons/Icon/NavigationIcons";
import { HallowImage } from "../HallowImage/HallowImage";

const buttonStyle: Interpolation = {
  alignItems: "center",
  borderRadius: radius.ms,
  columnGap: spacing.m,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  height: numericPixels[80],
  justifyContent: "flex-start",
  paddingBottom: spacing.s,
  paddingLeft: spacing.s,
  paddingRight: spacing.l,
  paddingTop: spacing.s,
};
const textOverrideStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHighest,
});
const descriptionOverrideStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsVeryHigh,
});
const imageStyle: Interpolation = {
  borderRadius: radius.s,
  objectFit: "cover",
  objectPosition: "center",
};
const textContainerStyle: Interpolation = {
  borderRadius: radius.s,
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  minWidth: spacing.none,
  width: spacing.full,
};
const descriptionStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
};

export type RecommendationsDeeplinkProps = Omit<DeeplinkSchema, "image_url"> & {
  onClick?: MouseEventHandler<any>;
  deeplinkType?: GetURLFromDeeplinkProps["type"];
  className?: string;
} & (
    | (Pick<DeeplinkSchema, "image_url"> & { images?: never })
    | { image_url?: string; images: Images }
  );

export const RecommendationDeeplink = ({
  id,
  color_hex,
  deeplink,
  title,
  image_url,
  description,
  className = "",
  onClick,
  deeplinkType = "path",
  images,
}: RecommendationsDeeplinkProps) => {
  const theme = useThemeValues();
  let override = false;
  if (color_hex === "#000000") {
    override = true;
    color_hex = theme.semanticColors.neutralsLowest;
  }

  if (!deeplink) return null;

  return (
    <Button
      key={`deeplink_${id}`}
      endIcon={<ChevronRightIcon css={override ? textOverrideStyle : null} />}
      variant={"color"}
      css={buttonStyle}
      className={className}
      color={color_hex}
      onClick={onClick}
      asChild
    >
      <Link
        key={`deeplink_${id}_url`}
        href={getURLFromDeeplink({ deeplink, type: deeplinkType }) ?? ""}
        target={"_top"}
      >
        <HallowImage
          key={`deeplink_${id}_image`}
          src={images ?? { small: image_url }}
          size={"s"}
          width={64}
          height={64}
          alt={title}
          css={imageStyle}
        />
        <div
          key={`deeplink_${id}_text`}
          css={[textContainerStyle, override ? textOverrideStyle : null]}
        >
          <Text
            key={`deeplink_${id}_title`}
            type={"title"}
            size={"m"}
            overflow={"ellipsis"}
          >
            {title}
          </Text>
          <Text
            key={`deeplink_${id}_description`}
            type={"detail"}
            size={"xl"}
            css={[descriptionStyle, override ? descriptionOverrideStyle : null]}
            overflow={"ellipsis"}
          >
            {description}
          </Text>
        </div>
      </Link>
    </Button>
  );
};
